import type {
  CreateInstitutionGroupPayload,
  InstitutionGroup,
  UpdateInstitutionGroupPayload,
} from '../../types/index.js';
import { baseApi } from '../base-api.js';

type GroupsResponse = { groups: InstitutionGroup[] };
type GroupResponse = { group: InstitutionGroup };
type GroupMutationResponse = { group: InstitutionGroup; message: string };

const groupListTags = (result: InstitutionGroup[] | undefined) =>
  result
    ? [...result.map(({ id }) => ({ type: 'Group' as const, id })), { type: 'Group' as const, id: 'LIST' }]
    : [{ type: 'Group' as const, id: 'LIST' }];

/**
 * Mirrors `apps/server/src/modules/institution-group` (`institutionGroupController`, prefix
 * `/institution-groups`). Group membership mutations (`addGroupMember`/`removeGroupMember`) only
 * touch the group they target — no other group's `members` list changes — so they invalidate
 * just that group's id plus `LIST` (the group's own membership count/preview may show on the
 * list view). See `institution-member.api.ts` for the reverse direction: deleting or renaming a
 * member invalidates `Group` LIST there, since a group's `members` are live-joined, not a
 * snapshot.
 */
export const institutionGroupApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getInstitutionGroups: builder.query<InstitutionGroup[], void>({
      query: () => ({ path: ['institution-groups'], method: 'get' }),
      transformResponse: (response: GroupsResponse) => response.groups,
      providesTags: (result) => groupListTags(result),
    }),

    getInstitutionGroupById: builder.query<InstitutionGroup, string>({
      query: (id) => ({ path: ['institution-groups', { param: 'group_id', value: id }], method: 'get' }),
      transformResponse: (response: GroupResponse) => response.group,
      providesTags: (_result, _error, id) => [{ type: 'Group', id }],
    }),

    createInstitutionGroup: builder.mutation<InstitutionGroup, CreateInstitutionGroupPayload>({
      query: (body) => ({ path: ['institution-groups'], method: 'post', body }),
      transformResponse: (response: GroupMutationResponse) => response.group,
      invalidatesTags: [{ type: 'Group', id: 'LIST' }],
    }),

    updateInstitutionGroup: builder.mutation<InstitutionGroup, { id: string; payload: UpdateInstitutionGroupPayload }>({
      query: ({ id, payload }) => ({
        path: ['institution-groups', { param: 'group_id', value: id }],
        method: 'put',
        body: payload,
      }),
      transformResponse: (response: GroupMutationResponse) => response.group,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Group', id },
        { type: 'Group', id: 'LIST' },
      ],
    }),

    deleteInstitutionGroup: builder.mutation<void, string>({
      query: (id) => ({ path: ['institution-groups', { param: 'group_id', value: id }], method: 'delete' }),
      transformResponse: (): void => undefined,
      invalidatesTags: (_result, _error, id) => [
        { type: 'Group', id },
        { type: 'Group', id: 'LIST' },
      ],
    }),

    addInstitutionGroupMember: builder.mutation<InstitutionGroup, { groupId: string; memberId: string }>({
      query: ({ groupId, memberId }) => ({
        path: ['institution-groups', { param: 'group_id', value: groupId }, 'members'],
        method: 'post',
        body: { member_id: memberId },
      }),
      transformResponse: (response: GroupMutationResponse) => response.group,
      invalidatesTags: (_result, _error, { groupId }) => [
        { type: 'Group', id: groupId },
        { type: 'Group', id: 'LIST' },
      ],
    }),

    removeInstitutionGroupMember: builder.mutation<void, { groupId: string; memberId: string }>({
      query: ({ groupId, memberId }) => ({
        path: [
          'institution-groups',
          { param: 'group_id', value: groupId },
          'members',
          { param: 'member_id', value: memberId },
        ],
        method: 'delete',
      }),
      transformResponse: (): void => undefined,
      invalidatesTags: (_result, _error, { groupId }) => [
        { type: 'Group', id: groupId },
        { type: 'Group', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetInstitutionGroupsQuery,
  useGetInstitutionGroupByIdQuery,
  useCreateInstitutionGroupMutation,
  useUpdateInstitutionGroupMutation,
  useDeleteInstitutionGroupMutation,
  useAddInstitutionGroupMemberMutation,
  useRemoveInstitutionGroupMemberMutation,
} = institutionGroupApi;
